import React, { useEffect, useState } from 'react';
import { Label } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import { CheckboxSelect } from '@atlaskit/select';
import type { ActionMeta } from '@atlassian/jira-common-components-picker/src/model';
import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import InfoPopup from '../info-popup';
import InfoPopupContent from './info-popup-content';
import messages from './messages';
import WarningSection from './warning-section';

type Props = {
	historicalProjectKeys: string[];
	releasedHistoricalKeys: string[];
	onReleaseProjectKey: (key: string, isReleased: boolean) => void;
	onUndoReleasedProjectKeys: () => void;
	projectType: ProjectType;
};

interface Option {
	label: string;
	value: string;
}

const HistoryKeysField = ({
	historicalProjectKeys,
	releasedHistoricalKeys,
	onReleaseProjectKey,
	onUndoReleasedProjectKeys,
	projectType,
}: Props) => {
	const isServiceDeskReleasePreviousKeysDisabled =
		projectType === SERVICE_DESK_PROJECT && !fg('hix-5543_enable_jsm_to_releasing_previous_keys');
	const releaseProjectKey = (_: unknown, selectedOption: ActionMeta<Option>) => {
		const isReleased = selectedOption.action !== 'select-option';
		const value = selectedOption.option?.value || selectedOption.removedValue?.value || '';

		onReleaseProjectKey(value, isReleased);
	};

	const mapKeysToOptions = (previousKeys: string[]) =>
		previousKeys.map((key) => ({
			label: key,
			value: key,
		}));

	const isOptionSelected = (option: Option) => !releasedHistoricalKeys.includes(option.value);
	const [selectedOptions, setSelectedOptions] = useState<Option[]>(
		mapKeysToOptions(historicalProjectKeys),
	);
	const [options, setOptions] = useState<Option[]>(mapKeysToOptions(historicalProjectKeys));

	useEffect(() => {
		const mappedKeys = mapKeysToOptions(historicalProjectKeys);

		setSelectedOptions(mappedKeys.filter(({ value }) => !releasedHistoricalKeys.includes(value)));
		setOptions(mappedKeys);
	}, [releasedHistoricalKeys, historicalProjectKeys]);

	const { formatMessage } = useIntl();

	return (
		<>
			<Label htmlFor="release-historical-keys-select">
				<>
					{formatMessage(messages.previousProjectKeysLabel)}
					<InfoPopup
						label={formatMessage(messages.infoTriggerLabel)}
						content={<InfoPopupContent />}
					/>
				</>
			</Label>
			<CheckboxSelect
				aria-label={formatMessage(messages.previousProjectKeysLabel)}
				id="release-historical-keys-select"
				options={options}
				value={selectedOptions}
				isOptionSelected={isOptionSelected}
				onChange={releaseProjectKey}
				placeholder={formatMessage(messages.selectPlaceholderLabel)}
				isClearable={false}
				isDisabled={isServiceDeskReleasePreviousKeysDisabled}
			/>
			{isServiceDeskReleasePreviousKeysDisabled && (
				<Box xcss={WarningMessageWrapper}>{formatMessage(messages.JsmProjectDisabledMessage)}</Box>
			)}
			{releasedHistoricalKeys.length > 0 && (
				<WarningSection onUndoReleasedProjectKeys={onUndoReleasedProjectKeys} />
			)}
		</>
	);
};

const WarningMessageWrapper = xcss({
	color: 'color.text.warning',
	fontSize: 'font.body.small',
});

export default HistoryKeysField;
